import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Paintbrush, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import painterImage from "@/assets/real-painter.jpg";

export const PaintingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Painting Services - Expert Painters & Decorators | Sortable"
        description="Find qualified painters and decorators near you. Interior and exterior painting, wallpapering, and decorating services. Fully insured professionals across the UK."
        keywords="painter, painting services, decorator, interior painting, exterior painting, wallpapering, decorating services, house painting, commercial painting, residential painting, spray painting, colour consultation, paint removal, surface preparation, gloss painting, emulsion painting, fence painting, door painting, window painting, local painter"
        canonical="https://sortable.lovable.app/services/painting"
        ogImage="https://sortable.lovable.app/real-painter.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Painting Services",
          "description": "Interior and exterior painting by qualified decorators",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Painting Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-600/10 border border-purple-600/20 text-purple-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Paintbrush className="h-5 w-5" />
            Professional Painting Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Expert Painters & Decorators
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Transform your property with professional painting and decorating services. Interior and exterior specialists offering quality workmanship and color consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Painters Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Paintbrush className="mr-2 h-5 w-5" />
                Join as a Painter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Painting & Decorating Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of qualified painters provides comprehensive decorating solutions for residential and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Interior Painting",
                description: "Professional interior painting for rooms, hallways, and commercial spaces",
                icon: <Paintbrush className="w-6 h-6" />
              },
              {
                title: "Exterior Painting",
                description: "Weather-resistant exterior painting for houses, fences, and buildings",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Wallpapering",
                description: "Expert wallpaper hanging and removal services with pattern matching",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Surface Preparation",
                description: "Professional surface preparation including sanding and priming",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Color Consultation",
                description: "Expert color advice and design consultation for your project",
                icon: <Paintbrush className="w-6 h-6" />
              },
              {
                title: "Spray Painting",
                description: "Professional spray painting for furniture and large surface areas",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-600/10 rounded-lg text-purple-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Painting Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our painters are experienced professionals using premium materials and techniques.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Premium Materials</h4>
                    <p className="text-muted-foreground">
                      Only high-quality paints and materials from trusted brands for lasting results.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Expert Technique</h4>
                    <p className="text-muted-foreground">
                      Professional painting techniques ensuring smooth, even coverage and clean lines.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Clean & Tidy</h4>
                    <p className="text-muted-foreground">
                      Professional approach with full protection of furniture and thorough cleanup.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Satisfaction Guarantee</h4>
                    <p className="text-muted-foreground">
                      Quality guarantee on all work with comprehensive insurance coverage.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={painterImage} 
                alt="Professional painter working on interior decoration"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Transform Your Space?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your painting project and get quotes from qualified, local painters and decorators. Professional results guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with qualified painters and decorators across the UK.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Interior Painting</li>
                <li>Exterior Painting</li>
                <li>Wallpapering</li>
                <li>Color Consultation</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Painters</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};